if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.activeIndex = 0;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchMoved = false;
        this.isInitialized = false;
        this.isTransitioning = false;
        this.lastActionAt = 0;

        this.handlePrevClick = this.handlePrevClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        this.setup();
      }

      setup() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        this.shell = this.querySelector('.product-media-modal__shell');
        this.content = this.querySelector('.product-media-modal__content');
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        this.closeButton = this.querySelector('.product-media-modal__toggle');
        this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
        this.counterTotal = this.querySelector('.product-media-modal__counter-total');

        this.refreshSlides();

        if (this.counterTotal) {
          this.counterTotal.textContent = String(this.slides.length);
        }

        this.prevButton?.addEventListener('click', this.handlePrevClick);
        this.nextButton?.addEventListener('click', this.handleNextClick);

        this.content?.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.content?.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        this.content?.addEventListener('touchend', this.handleTouchEnd, { passive: true });

        document.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.handlePrevClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);

        this.content?.removeEventListener('touchstart', this.handleTouchStart);
        this.content?.removeEventListener('touchmove', this.handleTouchMove);
        this.content?.removeEventListener('touchend', this.handleTouchEnd);

        document.removeEventListener('keydown', this.handleKeydown);

        if (super.disconnectedCallback) super.disconnectedCallback();
      }

      refreshSlides() {
        this.slides = Array.from(this.querySelectorAll('.product-media-modal__slide'));
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      handlePrevClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        this.requestGoTo(this.activeIndex - 1);
      }

      handleNextClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        this.requestGoTo(this.activeIndex + 1);
      }

      handleTouchStart(event) {
        if (!this.hasAttribute('open')) return;
        if (!event.touches || !event.touches.length) return;

        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchMoved = false;
      }

      handleTouchMove(event) {
        if (!this.hasAttribute('open')) return;
        if (!event.touches || !event.touches.length) return;

        const moveX = event.touches[0].clientX - this.touchStartX;
        const moveY = event.touches[0].clientY - this.touchStartY;

        if (Math.abs(moveX) > 8 || Math.abs(moveY) > 8) {
          this.touchMoved = true;
        }
      }

      handleTouchEnd(event) {
        if (!this.hasAttribute('open')) return;
        if (!this.touchMoved) return;
        if (!event.changedTouches || !event.changedTouches.length) return;

        const endX = event.changedTouches[0].clientX;
        const endY = event.changedTouches[0].clientY;

        const deltaX = endX - this.touchStartX;
        const deltaY = endY - this.touchStartY;

        const horizontalThreshold = 50;
        const verticalDominanceLimit = 0.8;

        if (Math.abs(deltaX) < horizontalThreshold) return;
        if (Math.abs(deltaY) > Math.abs(deltaX) * verticalDominanceLimit) return;

        if (deltaX > 0) {
          this.requestGoTo(this.activeIndex - 1);
        } else {
          this.requestGoTo(this.activeIndex + 1);
        }
      }

      handleKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.requestGoTo(this.activeIndex - 1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.requestGoTo(this.activeIndex + 1);
        }
      }

      showActiveMedia() {
        this.refreshSlides();
        if (!this.slides.length) return;

        const openedMediaId = this.openedBy?.getAttribute('data-media-id');
        let targetIndex = 0;

        if (openedMediaId) {
          const activeSlide = this.slides.find((slide) =>
            slide.querySelector(`[data-media-id="${openedMediaId}"]`)
          );
          if (activeSlide) {
            targetIndex = this.slides.indexOf(activeSlide);
          }
        }

        this.goTo(targetIndex, false);
      }

      requestGoTo(index) {
        const now = Date.now();

        if (this.isTransitioning) return;
        if (now - this.lastActionAt < 240) return;

        this.lastActionAt = now;
        this.isTransitioning = true;

        this.goTo(index, true);

        window.setTimeout(() => {
          this.isTransitioning = false;
        }, 240);
      }

      goTo(index, shouldFocusContent = true) {
        this.refreshSlides();
        if (!this.slides.length) return;

        const maxIndex = this.slides.length - 1;
        const safeIndex = Math.max(0, Math.min(index, maxIndex));

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === safeIndex;
          slide.classList.toggle('active', isActive);

          const mediaItems = slide.querySelectorAll('[data-media-id]');
          mediaItems.forEach((item) => {
            item.classList.toggle('active', isActive);
          });
        });

        this.activeIndex = safeIndex;
        this.updateCounter();
        this.updateNavState();
        this.loadDeferredMedia();

        if (shouldFocusContent && this.shell) {
          this.shell.focus({ preventScroll: true });
        }
      }

      updateCounter() {
        if (this.counterCurrent) {
          this.counterCurrent.textContent = String(this.activeIndex + 1);
        }

        if (this.counterTotal) {
          this.counterTotal.textContent = String(this.slides.length);
        }
      }

      updateNavState() {
        if (this.prevButton) {
          this.prevButton.disabled = this.activeIndex <= 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.activeIndex >= this.slides.length - 1;
        }
      }

      loadDeferredMedia() {
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }

        const activeSlide = this.slides[this.activeIndex];
        if (!activeSlide) return;

        const deferredMedia = activeSlide.querySelector('deferred-media');
        if (deferredMedia?.loadContent) {
          deferredMedia.loadContent(false);
        }
      }
    }
  );
}